import React from 'react';

import { faFacebook } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/macro';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import InputAdornment from '@mui/material/InputAdornment';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestoreIcon from '@mui/icons-material/RestoreOutlined';
import WarningIcon from '@mui/icons-material/Warning';

import Checkbox from '../../../misc/Checkbox';
import FormInlineButton from '../../../misc/FormInlineButton';
import Select from '../../../misc/Select';
import fbOAuth from '../../../utils/fbOAuth';
import { fetchYtTitles } from '../../../utils/ytdlp';
import { fetchYoutubeSnippet } from '../../../utils/youtube';

const id = 'facebook';
const name = 'Facebook Live';
const version = '1.0';
const stream_key_link = 'https://www.facebook.com/live/producer?ref=datarhei/restreamer';
const description = <Trans>Live-Streaming to Facebook Live RTMP service</Trans>;
const image_copyright = <Trans>More about licenses here</Trans>;
const author = {
	creator: { name: 'datarhei', link: 'https://github.com/datarhei' },
	maintainer: { name: 'datarhei', link: 'https://github.com/datarhei' },
};
const category = 'platform';
const requires = {
	protocols: ['rtmps'],
	formats: ['flv'],
	codecs: {
		audio: ['aac'],
		video: ['h264'],
	},
};

function ServiceIcon(props) {
	return <FontAwesomeIcon icon={faFacebook} style={{ color: '#2D88FF' }} {...props} />;
}

function init(settings) {
	return {
		stream_key_primary: '',
		stream_key_backup: '',
		rtmp_primary: true,
		rtmp_backup: false,
		_selected_fb_user_id: '',
		_selected_page_id: '',
		account_type: 'page',
		page_id: '',
		page_name: '',
		page_access_token: '',
		title: '',
		description: '',
		...settings,
	};
}

function AccountAvatar({ name, isPage }) {
	const initials = (name || '?').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
	return (
		<Avatar style={{ background: isPage ? '#2D88FF' : '#555', width: 36, height: 36, fontSize: '0.85rem' }}>
			{isPage ? '📄' : initials}
		</Avatar>
	);
}

function TokenBadge({ expired, tokenType, expiresAt }) {
	if (expired) {
		return (
			<Chip
				icon={<WarningIcon style={{ fontSize: '0.8rem' }} />}
				label="Token expirado"
				size="small"
				style={{ background: 'rgba(244,67,54,0.15)', color: '#f44336', border: '1px solid #f44336', fontSize: '0.7rem', height: 20 }}
			/>
		);
	}
	const label = tokenType === 'short'
		? '⚡ Token 2h'
		: expiresAt
			? `✅ ~${Math.max(0, Math.round((expiresAt - Date.now()) / 86400000))}d restantes`
			: '✅ Conectado';
	return (
		<Chip
			icon={<CheckCircleIcon style={{ fontSize: '0.8rem' }} />}
			label={label}
			size="small"
			style={{
				background: tokenType === 'short' ? 'rgba(255,152,0,0.15)' : 'rgba(76,175,80,0.15)',
				color:      tokenType === 'short' ? '#ff9800' : '#4caf50',
				border:     `1px solid ${tokenType === 'short' ? '#ff9800' : '#4caf50'}`,
				fontSize: '0.7rem', height: 20,
			}}
		/>
	);
}

function Service(props) {
	const settings = init(props.settings);

	if (!settings.title && props.metadata && props.metadata.name) {
		settings.title = props.metadata.name;
	}
	if (!settings.description && props.metadata && props.metadata.description) {
		settings.description = props.metadata.description;
	}

	const [$loading,     setLoading]     = React.useState(false);
	const [$authorizing, setAuthorizing] = React.useState(false);
	const [$error,       setError]       = React.useState('');
	const [$success,     setSuccess]     = React.useState('');
	const [$hasSecret,   setHasSecret]   = React.useState(false);
	const [$accounts,    setAccounts]    = React.useState([]);
	const [$selected,    setSelected]    = React.useState(null);

	const $appId = fbOAuth.getAppId();

	const buildList = React.useCallback((rawAccounts) => {
		const list = [];
		(rawAccounts || []).forEach((acc) => {
			const expired = fbOAuth.isTokenExpired(acc);
			const isShort = !acc.expires_at || (acc.expires_at - Date.now() < 3 * 60 * 60 * 1000);
			list.push({
				key:       acc.fb_user_id + '__user',
				fbUserId:  acc.fb_user_id,
				pageId:    '',
				name:      acc.name || 'Personal profile',
				isPage:    false,
				expired,
				tokenType: isShort ? 'short' : 'long',
				expiresAt: acc.expires_at || 0,
				scopes:    acc.scope_granted || [],
			});
			(acc.pages || []).forEach((page) => {
				list.push({
					key:       acc.fb_user_id + '__page__' + page.id,
					fbUserId:  acc.fb_user_id,
					pageId:    page.id,
					name:      page.name,
					isPage:    true,
					expired:   false,
					tokenType: 'long',
					expiresAt: 0,
					scopes:    [],
				});
			});
		});
		return list;
	}, []);

	const loadAccounts = React.useCallback(async () => {
		const cached = fbOAuth.listAccountsSync();
		setAccounts(buildList(cached));
		try {
			const fresh = await fbOAuth.listAccounts();
			const freshList = buildList(fresh);
			setAccounts(freshList);
			const prevKey = settings._selected_fb_user_id
				+ (settings._selected_page_id ? '__page__' + settings._selected_page_id : '__user');
			const found = freshList.find((e) => e.key === prevKey);
			if (found) setSelected(found);
		} catch (_) {}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	React.useEffect(() => {
		fbOAuth.getConfig().then(({ has_secret }) => setHasSecret(!!has_secret)).catch(() => {});
		loadAccounts();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const handleSelect = (entry) => {
		setError(''); setSuccess('');
		setSelected(entry);
		settings._selected_fb_user_id = entry.fbUserId;
		settings._selected_page_id    = entry.pageId;
		settings.page_id              = entry.pageId;
		settings.page_name            = entry.name;
		settings.account_type         = entry.isPage ? 'page' : 'user';
		props.onChange(createOutput(settings), settings);
	};

	const handleAddAccount = async () => {
		setError(''); setSuccess('');
		if (!$appId) { setError('⚠️ No hay App ID configurado. Ve a Settings → Integrations.'); return; }
		setAuthorizing(true);
		try {
			const context = {
				restreamer_channel_id:     props.channelId     || '',
				restreamer_publication_id: props.publicationId || '',
			};
			const { account, tokenType, expiresAt } = await fbOAuth.connectAccount($appId, $hasSecret, context);
			await loadAccounts();
			const typeLabel = tokenType === 'long'
				? `✅ Token de larga duración (~${Math.round((expiresAt - Date.now()) / 86400000)} días)`
				: '⚡ Token corto (2h) — configura App Secret para obtener tokens de 60 días';
			setSuccess(`Cuenta "${account.name}" conectada. ${typeLabel}`);
		} catch (err) {
			setError('❌ ' + err.message);
		} finally {
			setAuthorizing(false);
		}
	};

	const handleRefreshAccount = async (fbUserId) => {
		setError(''); setAuthorizing(true);
		try {
			const account = await fbOAuth.refreshAccount(fbUserId);
			await loadAccounts();
			setSuccess(`✅ Token renovado para "${account.name}" (~${Math.round((account.expires_at - Date.now()) / 86400000)} días).`);
		} catch (err) {
			if (!$appId) { setError('❌ ' + err.message); setAuthorizing(false); return; }
			try {
				const context = {
					restreamer_channel_id:     props.channelId     || '',
					restreamer_publication_id: props.publicationId || '',
				};
				const { account, tokenType } = await fbOAuth.connectAccount($appId, $hasSecret, context);
				await loadAccounts();
				setSuccess(`✅ Token renovado para "${account.name}". (${tokenType === 'long' ? '60 días' : '2h'})`);
			} catch (err2) { setError('❌ ' + err2.message); }
		} finally { setAuthorizing(false); }
	};

	const handleRemoveAccount = async (fbUserId) => {
		await fbOAuth.removeAccount(fbUserId);
		if ($selected && $selected.fbUserId === fbUserId) {
			setSelected(null);
			settings._selected_fb_user_id = '';
			settings._selected_page_id    = '';
			settings.page_access_token    = '';
			settings.page_id              = '';
			settings.page_name            = '';
			props.onChange(createOutput(settings), settings);
		}
		await loadAccounts();
	};

	const handleChange = (what) => (event) => {
		const value = event && event.target ? event.target.value : event;
		setError(''); setSuccess('');
		if (['rtmp_primary', 'rtmp_backup'].includes(what)) {
			settings[what] = !settings[what];
		} else {
			settings[what] = value;
		}
		props.onChange(createOutput(settings), settings);
	};

	const createOutput = (s) => {
		const outputs = [];
		if (s.stream_key_primary && s.rtmp_primary)
			outputs.push({ address: 'rtmps://live-api-s.facebook.com:443/rtmp/' + s.stream_key_primary, options: ['-f', 'flv'] });
		if (s.stream_key_backup && s.rtmp_backup)
			outputs.push({ address: 'rtmps://live-api-s.facebook.com:443/rtmp/' + s.stream_key_backup, options: ['-f', 'flv'] });
		return outputs;
	};

	const extractKey = (streamUrl) => {
		if (!streamUrl) return '';
		const parts = streamUrl.split('/');
		return parts[parts.length - 1] || '';
	};

	const handleStartLive = async () => {
		setError(''); setSuccess('');
		if (!$selected) { setError('Selecciona una cuenta conectada.'); return; }
		setLoading(true);
		try {
			const token = await fbOAuth.getTokenForEntry($selected.fbUserId, $selected.pageId);
			if (!token) { setError('No se pudo obtener el token. Reconecta la cuenta.'); return; }

			const subject = $selected.isPage ? encodeURIComponent($selected.pageId) : 'me';
			const form    = new URLSearchParams();
			if (settings.title)       form.append('title',       settings.title);
			if (settings.description) form.append('description', settings.description);
			form.append('status',       'LIVE_NOW');
			form.append('access_token', token);

			const resp = await fetch(`https://graph.facebook.com/v19.0/${subject}/live_videos`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
				body: form.toString(),
			});
			const data = await resp.json();

			if (!resp.ok || data.error) {
				const code = data.error ? data.error.code : resp.status;
				const msg  = data.error ? data.error.message : `HTTP ${resp.status}`;
				if (code === 190)                     setError('⚠️ Token expirado (#190). Usa 🔄 para renovar.');
				else if (code === 100)                setError('⚠️ Error #100: ' + msg);
				else if (code === 200 || code === 10) setError(`⚠️ Sin permiso (${code}): reconecta con "publish_video".`);
				else                                  setError(`❌ Facebook API [#${code}]: ${msg}`);
				return;
			}

			const streamUrl = data.stream_url || data.secure_stream_url || '';
			if (!streamUrl) { setError('Facebook no devolvió stream URL. Verifica permiso "publish_video".'); return; }

			settings.stream_key_primary = extractKey(streamUrl);
			props.onChange(createOutput(settings), settings);
			setSuccess(`✅ Live creado! Stream key configurado. Live ID: ${data.id}`);
		} catch (err) {
			setError('❌ Error: ' + err.message);
		} finally {
			setLoading(false);
		}
	};

	const isSelectedEntry = (entry) => $selected && $selected.key === entry.key;

	return (
		<Grid container spacing={2}>

			{/* ══ Cuentas conectadas ══ */}
			<Grid item xs={12}>
				<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
					<Typography variant="h4" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
						<FontAwesomeIcon icon={faFacebook} style={{ color: '#2D88FF' }} />
						Facebook connected accounts
					</Typography>
					<Button
						variant="outlined" size="small"
						startIcon={$authorizing ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
						onClick={handleAddAccount} disabled={$authorizing || !$appId}
						style={{ borderColor: '#2D88FF', color: '#2D88FF', textTransform: 'none' }}
					>
						{$authorizing ? 'Connecting…' : '+ Add account'}
					</Button>
				</div>

				{!$appId && (
					<Typography variant="caption" style={{ color: '#f44336', display: 'block', marginBottom: 8 }}>
						⚠️ No App ID configured. Go to Settings → Integrations → Facebook App ID.
					</Typography>
				)}

				<Typography variant="caption" style={{ color: $hasSecret ? '#4caf50' : '#ff9800', display: 'block', marginBottom: 4 }}>
					{$hasSecret
						? '🔐 App Secret configured — 60-day tokens active'
						: '⚡ No App Secret — 2h tokens (configure App Secret for 60-day tokens)'}
				</Typography>

				{$accounts.length === 0 ? (
					<div style={{ border: '1px dashed rgba(255,255,255,0.15)', borderRadius: 8, padding: '18px 16px', textAlign: 'center' }}>
						<FontAwesomeIcon icon={faFacebook} style={{ fontSize: '1.8rem', marginBottom: 8, color: '#333' }} />
						<Typography variant="body2" style={{ color: '#666' }}>
							No accounts connected. Click "+ Add account".
						</Typography>
					</div>
				) : (
					<List dense style={{ padding: 0 }}>
						{$accounts.map((entry) => {
							const sel = isSelectedEntry(entry);
							return (
								<ListItem key={entry.key} button onClick={() => handleSelect(entry)}
									style={{
										borderRadius: 8, marginBottom: 4, cursor: 'pointer',
										border:     sel ? '1px solid #2D88FF' : '1px solid rgba(255,255,255,0.08)',
										background: sel ? 'rgba(45,136,255,0.1)' : 'rgba(255,255,255,0.03)',
									}}
								>
									<ListItemAvatar style={{ minWidth: 44 }}>
										<AccountAvatar name={entry.name} isPage={entry.isPage} />
									</ListItemAvatar>
									<ListItemText
										primary={
											<span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
												<span style={{ fontWeight: sel ? 700 : 400, color: sel ? '#fff' : '#ccc', fontSize: '0.9rem' }}>
													{entry.name}
												</span>
												<TokenBadge expired={entry.expired} tokenType={entry.tokenType} expiresAt={entry.expiresAt} />
												{sel && <Chip label="Selected" size="small" style={{ background: '#2D88FF', color: '#fff', fontSize: '0.68rem', height: 18 }} />}
											</span>
										}
										secondary={
											<span style={{ fontSize: '0.75rem', color: '#777' }}>
												{entry.isPage ? `📄 Page · ID: ${entry.pageId}` : `👤 Profile · ID: ${entry.fbUserId}`}
												{entry.expiresAt && !entry.isPage ? ` · Expires: ${fbOAuth.formatExpiry(entry.expiresAt)}` : ''}
												{entry.scopes && entry.scopes.length > 0 ? ` · Scopes: ${entry.scopes.join(', ')}` : ''}
											</span>
										}
									/>
									<Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 1 }}>
										{!entry.isPage && (
											<Tooltip title="Refresh token (60 days)">
												<span>
													<IconButton size="small" disabled={$authorizing}
														onClick={(e) => { e.stopPropagation(); handleRefreshAccount(entry.fbUserId); }}
														style={{ color: '#ff9800' }}
													>
														<RefreshIcon fontSize="small" />
													</IconButton>
												</span>
											</Tooltip>
										)}
										{!entry.isPage && (
											<Tooltip title="Disconnect account">
												<IconButton size="small"
													onClick={(e) => { e.stopPropagation(); handleRemoveAccount(entry.fbUserId); }}
													style={{ color: '#f44336' }}
												>
													<DeleteIcon fontSize="small" />
												</IconButton>
											</Tooltip>
										)}
									</Box>
								</ListItem>
							);
						})}
					</List>
				)}
			</Grid>

			{$error && (
				<Grid item xs={12}>
					<Typography variant="body2" style={{ color: '#f44336', whiteSpace: 'pre-wrap', fontSize: '0.82rem', background: 'rgba(244,67,54,0.07)', padding: '8px 12px', borderRadius: 6, border: '1px solid rgba(244,67,54,0.3)' }}>
						{$error}
					</Typography>
				</Grid>
			)}
			{$success && (
				<Grid item xs={12}>
					<Typography variant="body2" style={{ color: '#4caf50', fontSize: '0.82rem', background: 'rgba(76,175,80,0.07)', padding: '8px 12px', borderRadius: 6, border: '1px solid rgba(76,175,80,0.3)' }}>
						{$success}
					</Typography>
				</Grid>
			)}

			<Grid item xs={12}><Divider style={{ borderColor: 'rgba(255,255,255,0.08)' }} /></Grid>

			{/* ══ Configuración de stream ══ */}
			<Grid item xs={12}>
				<Typography variant="h4" style={{ marginBottom: 4 }}>Stream settings</Typography>
			</Grid>

			{/* YouTube URL input removed — Reset will restore from stored metadata */}

			<Grid item xs={12}>
				<TextField variant="outlined" fullWidth
					label="Live title"
					placeholder={props.metadata?.name || 'My live stream'}
					value={settings.title}
					onChange={handleChange('title')}
					InputProps={props.metadata?.name ? {
						endAdornment: (
							<InputAdornment position="end">
								<Tooltip title="Reset to stream title">
									<IconButton size="small" onClick={async () => {
											const meta = props.metadata || {};
											const t = meta.name || meta.title || '';
											const d = meta.description || '';
											if (!t && !d) return;
											if (t) settings.title = t;
											if (d) settings.description = d;
											props.onChange(createOutput(settings), settings);
											try {
												if (props.restreamer && typeof props.restreamer.SetEgressMetadata === 'function') {
													await props.restreamer.SetEgressMetadata(props.channelId, props.publicationId, { name: settings.title || '', description: settings.description || '' });
												}
											} catch (err) {
												console.warn('[reset] failed to save metadata', err);
											}
										}}>
										<RestoreIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							</InputAdornment>
						),
					} : undefined}
				/>
			</Grid>

			<Grid item xs={12}>
				<TextField variant="outlined" fullWidth multiline rows={2}
					label="Description"
					placeholder={props.metadata?.description || ''}
					value={settings.description}
					onChange={handleChange('description')}
					InputProps={props.metadata?.description ? {
						endAdornment: (
							<InputAdornment position="end" style={{ alignSelf: 'flex-start', marginTop: 10 }}>
								<Tooltip title="Reset to stream description">
									<IconButton size="small" onClick={async () => {
											const meta = props.metadata || {};
											const t = meta.name || meta.title || '';
											const d = meta.description || '';
											if (!t && !d) return;
											if (t) settings.title = t;
											if (d) settings.description = d;
											props.onChange(createOutput(settings), settings);
											try {
												if (props.restreamer && typeof props.restreamer.SetEgressMetadata === 'function') {
													await props.restreamer.SetEgressMetadata(props.channelId, props.publicationId, { name: settings.title || '', description: settings.description || '' });
												}
											} catch (err) {
												console.warn('[reset] failed to save metadata', err);
											}
										}}>
										<RestoreIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							</InputAdornment>
						),
					} : undefined}
				/>
			</Grid>

			<Grid item xs={12}>
				<Button variant="contained" fullWidth onClick={handleStartLive}
					disabled={$loading || !$selected}
					style={{ background: $loading ? '#333' : '#1877f2', color: '#fff', textTransform: 'none', fontSize: '1rem', padding: '10px 0', borderRadius: 8 }}
					startIcon={$loading ? <CircularProgress size={18} color="inherit" /> : <FontAwesomeIcon icon={faFacebook} />}
				>
					{$loading
						? 'Creating stream…'
						: $selected
							? `Start Live as "${$selected.name}"`
							: 'Select an account first'}
				</Button>
				{$selected && $selected.expired && (
					<Typography variant="caption" style={{ color: '#ff9800', display: 'block', marginTop: 4 }}>
						⚠️ Token is expired. Use 🔄 to refresh it.
					</Typography>
				)}
				{$selected && $selected.tokenType === 'short' && (
					<Typography variant="caption" style={{ color: '#ff9800', display: 'block', marginTop: 4 }}>
						⚡ Short-lived token (2h). Configure App Secret for 60-day tokens.
					</Typography>
				)}
			</Grid>

			<Grid item xs={12}><Divider style={{ borderColor: 'rgba(255,255,255,0.08)' }} /></Grid>

			{/* ══ Stream keys ══ */}
			<Grid item xs={12}>
				<Typography variant="h4"><Trans>Stream Keys</Trans></Typography>
				<Typography variant="caption" style={{ color: '#777' }}>
					Filled when Live is created. You can also paste them manually.
				</Typography>
			</Grid>

			{settings.rtmp_primary && (
				<>
					<Grid item xs={12} md={9}>
						<TextField variant="outlined" fullWidth label={<Trans>Stream key principal</Trans>}
							value={settings.stream_key_primary} onChange={handleChange('stream_key_primary')} />
					</Grid>
					<Grid item xs={12} md={3}>
						<FormInlineButton target="blank" href={stream_key_link} component="a"><Trans>GET</Trans></FormInlineButton>
					</Grid>
				</>
			)}
			{settings.rtmp_backup && (
				<>
					<Grid item xs={12} md={9}>
						<TextField variant="outlined" fullWidth label={<Trans>Stream key backup</Trans>}
							value={settings.stream_key_backup} onChange={handleChange('stream_key_backup')} />
					</Grid>
					<Grid item xs={12} md={3}>
						<FormInlineButton target="blank" href={stream_key_link} component="a"><Trans>GET</Trans></FormInlineButton>
					</Grid>
				</>
			)}

			<Grid item xs={12}>
				<Checkbox label="Enable primary stream" checked={settings.rtmp_primary} onChange={handleChange('rtmp_primary')} />
				<Checkbox label="Enable backup stream"  checked={settings.rtmp_backup}  onChange={handleChange('rtmp_backup')} />
			</Grid>

			<Grid item xs={12}><Divider style={{ borderColor: 'rgba(255,255,255,0.08)', marginTop: 4 }} /></Grid>

			{/* ══ Token manual (avanzado) ══ */}
			<Grid item xs={12}>
				<Typography variant="caption" style={{ color: '#555' }}>
					Advanced: manual token (only if not using a connected account)
				</Typography>
			</Grid>

			<Grid item xs={12} md={settings.account_type === 'page' ? 6 : 12}>
				<Select label="Account type" value={settings.account_type} onChange={handleChange('account_type')}>
					<MenuItem value="page">Facebook Page</MenuItem>
					<MenuItem value="user">Personal profile</MenuItem>
				</Select>
			</Grid>

			{settings.account_type === 'page' && (
				<Grid item xs={12} md={6}>
					<TextField variant="outlined" fullWidth label="Page ID"
						placeholder="123456789012345" value={settings.page_id} onChange={handleChange('page_id')} />
				</Grid>
			)}

			<Grid item xs={12}>
				<TextField variant="outlined" fullWidth
					label={settings.account_type === 'page' ? 'Page Access Token' : 'User Access Token'}
					placeholder="EAAxxxxxxx…"
					value={settings.page_access_token}
					onChange={handleChange('page_access_token')}
				/>
			</Grid>

		</Grid>
	);
}

Service.defaultProps = {
	settings: {},
	skills: {},
	metadata: {},
	streams: [],
	onChange: function (output, settings) {},
};

export { id, name, version, stream_key_link, description, image_copyright, author, category, requires, ServiceIcon as icon, Service as component };

